/**
 * Soft-deleted contacts (PRD §5.1, §10.7).
 *
 * Admin-only landing page that lists trashed contacts and lets the Admin
 * restore them. Restore can 409 if the phone has been re-used in the
 * meantime — same shapes as POST/PATCH, surfaced inline.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { BackLink } from "../components/BackLink";

import { ApiError, type TrashedContact, contactsApi, isDuplicatePhoneDetail } from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/contacts/trash")({
  // Soft front-end gate: keeps non-admins from accidentally landing on a
  // page that would 403 anyway. The server is still the source of truth.
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin") {
      throw redirect({ to: "/contacts" });
    }
  },
  component: TrashPage,
});

function TrashPage() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["contacts-trash", { limit: 50, offset: 0 }],
    queryFn: () => contactsApi.listTrash({ limit: 50, offset: 0 }),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/contacts" fallbackLabel="contacts" />
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Trash2 className="size-5 opacity-70" aria-hidden />
          Trash
        </h1>
        <p className="text-sm opacity-60">
          Soft-deleted contacts. Restoring brings back the row and re-enforces phone uniqueness — if
          the number was reused, you'll get a conflict to resolve.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load trash: {(error as Error).message}
        </div>
      ) : !data || data.items.length === 0 ? (
        <Empty />
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {data.items.map((c) => (
            <TrashRow key={c.id} contact={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Empty() {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Trash2 className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">Trash is empty</p>
      <p className="mt-1 text-sm opacity-60">Deleted contacts will appear here.</p>
    </div>
  );
}

function TrashRow({ contact }: { contact: TrashedContact }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [duplicateExistingId, setDuplicateExistingId] = useState<string | null>(null);

  const restore = useMutation({
    mutationFn: () => contactsApi.restore(contact.id),
    onSuccess: (restored) => {
      queryClient.setQueryData(["contact", contact.id], restored);
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-trash"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isDuplicatePhoneDetail(err.detail)) {
        if (err.detail.code === "duplicate_phone_visible") {
          setDuplicateExistingId(err.detail.existing_contact_id);
          setError(
            "Can't restore: the phone number is now in use by another contact. Open it to decide what to do.",
          );
        } else {
          setError(err.detail.message);
        }
        return;
      }
      if (err instanceof ApiError && err.reauthRequired) {
        setError("For security, please log out and log back in to confirm this restore.");
        return;
      }
      setError(err instanceof ApiError ? err.message : "Could not restore contact.");
    },
  });

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="truncate text-sm font-medium">{contact.name}</div>
        <div className="font-mono text-xs opacity-70">{contact.phone}</div>
        <div className="text-xs opacity-50">
          Deleted {new Date(contact.deleted_at).toLocaleString()}
        </div>
        {error && (
          <div className="mt-1 text-xs text-red-700 dark:text-red-300">
            {error}
            {duplicateExistingId && (
              <>
                {" — "}
                <Link
                  to="/contacts/$contactId"
                  params={{ contactId: duplicateExistingId }}
                  className="underline"
                >
                  open the conflicting contact
                </Link>
              </>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        disabled={restore.isPending}
        onClick={() => {
          setError(null);
          setDuplicateExistingId(null);
          restore.mutate();
        }}
        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm font-medium hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
      >
        <RotateCcw className="size-4" aria-hidden />
        {restore.isPending ? "Restoring…" : "Restore"}
      </button>
    </li>
  );
}
